import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, rupiah, LOGO_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

export default function PaymentReturn() {
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const [info, setInfo] = useState(null);
  const polled = useRef(0);
  const [sid, setSid] = useState(null);
  const [dl, setDl] = useState(false);

  const downloadInvoice = async () => {
    if (!sid) return;
    setDl(true);
    try {
      const r = await api.get(`/payments/invoice/${sid}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `invoice_${sid}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh invoice"); }
    finally { setDl(false); }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("failed"); return; }
    setSid(sessionId);

    const poll = async () => {
      if (polled.current >= 8) { setState("timeout"); return; }
      polled.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setState("success"); return; }
        if (data.status === "expired") { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, []);

  const icons = {
    checking: <Loader2 size={56} className="text-emerald-500 animate-spin" />,
    success: <CheckCircle2 size={56} className="text-emerald-500" />,
    failed: <XCircle size={56} className="text-rose-500" />,
    timeout: <Loader2 size={56} className="text-amber-500" />,
  };
  const titles = {
    checking: "Memeriksa pembayaran…",
    success: "Pembayaran Berhasil!",
    failed: "Pembayaran Gagal",
    timeout: "Masih Diproses",
  };
  const subs = {
    checking: "Mohon tunggu sebentar.",
    success: info ? `${info.description} sebesar ${rupiah(info.amount)} telah diterima.` : "Saldo/tagihan telah diperbarui.",
    failed: "Pembayaran dibatalkan atau gagal. Tidak ada saldo terpotong.",
    timeout: "Status pembayaran belum final. Cek kembali beberapa saat lagi.",
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
      <div className="bg-white border border-stone-200 rounded-2xl shadow-lg p-10 max-w-md w-full text-center animate-fade-up">
        <img src={LOGO_URL} alt="" className="w-14 h-14 mx-auto mb-6 object-contain" />
        <div className="flex justify-center mb-5">{icons[state]}</div>
        <h1 className="font-heading text-2xl font-bold text-stone-900" data-testid="payment-result-title">{titles[state]}</h1>
        <p className="text-stone-500 mt-2">{subs[state]}</p>
        {state === "success" && (
          <Button onClick={downloadInvoice} disabled={dl} variant="outline" data-testid="download-invoice-button" className="mt-6 w-full py-6 rounded-xl">
            {dl ? <Loader2 size={18} className="animate-spin" /> : (<><FileText size={18} className="mr-2" /> Unduh Invoice / Bukti Pembayaran</>)}
          </Button>
        )}
        <Button onClick={() => navigate("/parent")} data-testid="back-to-dashboard" className="mt-3 w-full py-6 rounded-xl bg-emerald-600 hover:bg-emerald-700">
          Kembali ke Beranda
        </Button>
      </div>
    </div>
  );
}
